import { ArrowLeft } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { navigateWithThreadPreservation } from '@/utils/thread-navigation'
import { cn } from '@/lib/utils'

export interface BackButtonProps {
  /** e.g. "Back to jobs" — name the destination list. Used as visible text or `aria-label` when icon-only. */
  label: string
  /** Destination path; navigates with thread preservation. Ignored when `onClick` is set. */
  to?: string
  /** Custom handler (e.g. history-aware back); takes precedence over `to`. */
  onClick?: () => void
  /** Passed through when navigating with `to` (e.g. restore list scroll). */
  state?: unknown
  /** Icon-only affordance for compact headers; label is exposed to assistive tech and `title`. */
  iconOnly?: boolean
  className?: string
}

/**
 * Yes Human preferred page-level back affordance for first-level detail pages (reached
 * directly from a list). Second-level+ pages use {@link PageBreadcrumbs} instead.
 */
export function BackButton({
  label,
  to,
  onClick,
  state,
  iconOnly = false,
  className,
}: BackButtonProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const handleClick = () => {
    if (onClick) {
      onClick()
      return
    }
    if (to) {
      navigateWithThreadPreservation(
        navigate,
        to,
        searchParams,
        state !== undefined ? { state } : undefined,
      )
    }
  }

  if (iconOnly) {
    return (
      <Button
        type="button"
        variant="ghostToolbar"
        size="iconSm"
        className={cn(className)}
        onClick={handleClick}
        aria-label={label}
        title={label}
      >
        <ArrowLeft className="h-4 w-4" />
      </Button>
    )
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="inline"
      className={cn(className)}
      onClick={handleClick}
    >
      <ArrowLeft className="h-4 w-4" />
      {label}
    </Button>
  )
}
